import { RouterSingleton } from '@/router/RouterSingleton'
import { useAuthStore, AuthPermission, authPermissionSatisfies, AuthScope, PermissionLevel } from '@shared/stores/auth'
import { useConfigStore } from '@shared/stores/config'
import { authGuard } from '@auth0/auth0-vue'
import { useLocalStorage } from '@vueuse/core'
import { NavigationGuardNext, RouteLocationGeneric, RouteLocationNormalized, RouteParamsRawGeneric, RouteRecordNameGeneric } from 'vue-router'
import { RETURN_PATH_KEY } from '@shared/helpers/AuthorizationParametersHelper'

export interface Navigator {
    navigateToInitialRoute: (from?: RouteLocationGeneric) => Promise<void>
    scopedAuthGuard: (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext, requiredPermissions: AuthPermission[] | undefined, unAuthorizedRedirectName: string) => Promise<void>
    pushRouteWithHomeFallback: (routeName: string) => void
}

export const redirectNameLocalStorageKey = 'redirect_name'

type FromRoute = { fullPath: string; name: string | RouteRecordNameGeneric | null }
export function useNavigator(): Navigator {
    const router = RouterSingleton.getRouter()
    const authStore = useAuthStore()
    const configStore = useConfigStore()
    const redirectNameLocalStorage = useLocalStorage<string | undefined>(redirectNameLocalStorageKey, undefined)
    const returnPathLocalStorage = useLocalStorage<string | undefined>(RETURN_PATH_KEY, undefined)

    async function navigateToInitialRoute(from?: RouteLocationGeneric) {
        await router.isReady()

        if (redirectNameLocalStorage.value !== undefined) {
            const redirectName = redirectNameLocalStorage.value
            redirectNameLocalStorage.value = undefined
            const navigated = await navigateIfValidRoute({ fullPath: '', name: redirectName })
            if (navigated) return
        }

        if (from !== undefined) {
            const navigated = await navigateIfValidRoute(from)
            if (navigated) return
        }

        if (returnPathLocalStorage.value !== undefined && shouldRedirectFromCurrentRoute()) {
            navigateToInsights()
            return
        }

        try {
            if (
                authStore.authScope.some(permission => authPermissionSatisfies(permission, { level: PermissionLevel.All, scope: AuthScope.Operational })) &&
                authStore.instanceName.toLowerCase() === 'skynamo'
            ) {
                await navigateIfValidRoute({ fullPath: '/support-admin', name: 'SupportAdmin' })
            } else if (authStore.authScope.some(permission => authPermissionSatisfies(permission, { level: PermissionLevel.All, scope: AuthScope.Client }))) {
                await navigateIfValidRoute({ fullPath: '/subscription-management', name: 'SubscriptionManagement' })
            } else if (authStore.authScope.some(permission => authPermissionSatisfies(permission, { level: PermissionLevel.All, scope: AuthScope.Instance }))) {
                await navigateIfValidRoute({ fullPath: '/subscription-management/manage-instances', name: 'SubscriptionManagementManageInstances' })
            } else if (authStore.authScope.some(permission => authPermissionSatisfies(permission, { level: PermissionLevel.Read, scope: AuthScope.AnalyticsDashboard }))) {
                await navigateIfValidRoute({ fullPath: '/analytics-dashboard', name: 'AnalyticsDashboard' })
            } else {
                navigateToInsights()
            }
        } catch (error) {
            if (configStore.env !== 'production') {
                console.error('Could not navigate to route.', error)
            }
        }
    }

    async function navigateIfValidRoute(from: FromRoute) {
        if (!shouldRedirectFromCurrentRoute()) return true

        if (from.name !== null && from.name !== undefined && router.hasRoute(from.name)) {
            pushRouteWithHomeFallback(from.name.toString())
            return true
        }

        return false
    }

    function navigateToInsights() {
        if (shouldRedirectFromCurrentRoute()) {
            const returnPath = returnPathLocalStorage.value
            returnPathLocalStorage.value = undefined
            window.location.href = returnPath && authStore.insightsUrl
                ? `${authStore.insightsUrl}${returnPath}`
                : authStore.insightsUrl
        }
    }

    function shouldRedirectFromCurrentRoute(): boolean {
        return router.currentRoute.value.name === 'Home' || router.currentRoute.value.name === 'SilentAuth'
    }

    async function scopedAuthGuard(to: RouteLocationNormalized, _from: RouteLocationNormalized, next: NavigationGuardNext, requiredPermissions: AuthPermission[] | undefined, unAuthorizedRedirectName: string) {
        const isAuthenticated = await authGuard(to)
        const authStore = useAuthStore()
        await authStore.update()

        const isAuthorized = requiredPermissions?.length === 0 || requiredPermissions?.every(requiredPermission => authStore.authScope.some(tokenPermission => authPermissionSatisfies(tokenPermission, requiredPermission)))
        if (isAuthenticated && isAuthorized) {
            next()
        } else if (to.meta.fallbackRedirect !== undefined) {
            next({ name: to.meta.fallbackRedirect })
        } else {
            next({ name: unAuthorizedRedirectName })
        }
    }

    function pushRouteWithHomeFallback(routeName: string, params?: RouteParamsRawGeneric) {
        if (router.hasRoute(routeName)) {
            router.push({ name: routeName, params })
        } else {
            router.push({ name: 'Home', params })
        }
    }

    return {
        navigateToInitialRoute,
        scopedAuthGuard,
        pushRouteWithHomeFallback,
    }
}

export default useNavigator
